// AnalyticsView.tsx
// Main analytics view with charts and KPIs

import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { BellDot, BarChart2, Inbox, Loader2 } from "lucide-react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Legend,
    CartesianGrid,
    BarChart,
    Bar,
} from "recharts";
import { CHART_COLORS, NOTIFICATION_SOURCES, type NotificationSource } from "./constants";
import { aggregate, getDateRange, type AnalyticsDataPoint } from "./utils";
import { trpc } from "~/api/trpc";

const KPI = ({ label, value, isLoading }: { label: string; value: number | string; isLoading?: boolean }) => (
    <Card className="rounded-2xl">
        <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">{label}</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
            {isLoading ? (
                <div className="flex items-center gap-2">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
            ) : (
                <div className="text-3xl font-semibold">{value}</div>
            )}
        </CardContent>
    </Card>
);

const GlobalOptInSummary = ({ 
    totalUsers, 
    optInUsers, 
    optOutUsers, 
    isLoading 
}: { 
    totalUsers: number; 
    optInUsers: number; 
    optOutUsers: number;
    isLoading: boolean;
}) => (
    <Card className="rounded-2xl border-primary/20">
        <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
                <BellDot className="h-4 w-4" /> App users — Notification permission
            </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div>
                <div className="text-xs text-muted-foreground">Total users</div>
                {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mt-1" />
                ) : (
                    <div className="text-2xl font-semibold">{totalUsers.toLocaleString()}</div>
                )}
            </div>
            <div>
                <div className="text-xs text-muted-foreground">Opt-in</div>
                {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mt-1" />
                ) : (
                    <div className="text-2xl font-semibold text-emerald-600">
                        {optInUsers.toLocaleString()}
                    </div>
                )}
            </div>
            <div>
                <div className="text-xs text-muted-foreground">Opt-out</div>
                {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground mt-1" />
                ) : (
                    <div className="text-2xl font-semibold text-rose-600">
                        {optOutUsers.toLocaleString()}
                    </div>
                )}
            </div>
        </CardContent>
    </Card>
);

const AnalyticsLineChart = ({ data, isLoading }: { data: AnalyticsDataPoint[]; isLoading: boolean }) => (
    <Card className="rounded-2xl">
        <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
                <BarChart2 className="h-4 w-4" /> Performance (Line)
            </CardTitle>
        </CardHeader>
        <CardContent>
            <div className="h-64 w-full sm:h-72 md:h-80">
                {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                ) : data.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                        No data available for the selected filters
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tickMargin={8} interval="preserveStartEnd" />
                            <YAxis width={40} />
                            <Tooltip />
                            <Legend wrapperStyle={{ paddingTop: 8 }} />
                            <Line type="monotone" dataKey="sent" name="Sent" stroke={CHART_COLORS.sent} strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="delivered" name="Delivered" stroke={CHART_COLORS.delivered} strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="opened" name="Opened" stroke={CHART_COLORS.opened} strokeWidth={2} dot={false} />
                            <Line type="monotone" dataKey="unsubscribed" name="Unsubscribed" stroke={CHART_COLORS.unsubscribed} strokeWidth={2} dot={false} />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </CardContent>
    </Card>
);

const AnalyticsBarChart = ({ data, isLoading }: { data: AnalyticsDataPoint[]; isLoading: boolean }) => (
    <Card className="rounded-2xl">
        <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
                <BarChart2 className="h-4 w-4" /> Performance (Bars)
            </CardTitle>
        </CardHeader>
        <CardContent>
            <div className="h-64 w-full sm:h-72 md:h-80">
                {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                ) : data.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                        No data available for the selected filters
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="key" tickMargin={8} interval="preserveStartEnd" />
                            <YAxis width={40} />
                            <Tooltip />
                            <Legend wrapperStyle={{ paddingTop: 8 }} />
                            <Bar dataKey="sent" name="Sent" stackId="a" fill={CHART_COLORS.sent} />
                            <Bar dataKey="delivered" name="Delivered" stackId="a" fill={CHART_COLORS.delivered} />
                            <Bar dataKey="opened" name="Opened" stackId="a" fill={CHART_COLORS.opened} />
                            <Bar dataKey="unsubscribed" name="Unsubscribed" stackId="a" fill={CHART_COLORS.unsubscribed} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </CardContent>
    </Card>
);

const AnalyticsConversions = ({ kpis, isLoading }: { kpis: { sent: number; delivered: number; opened: number; unsubscribed: number }; isLoading: boolean }) => {
    const sent = Math.max(kpis.sent, 1);
    const delivered = Math.max(kpis.delivered, 1);

    const rows = [
        { step: "Sent", pctPrev: 100, pctTotal: 100 },
        {
            step: "Delivered",
            pctPrev: Math.round((kpis.delivered / sent) * 100),
            pctTotal: Math.round((kpis.delivered / sent) * 100),
        },
        {
            step: "Opened",
            pctPrev: Math.round((kpis.opened / delivered) * 100),
            pctTotal: Math.round((kpis.opened / sent) * 100),
        },
        {
            step: "Unsubscribed",
            pctPrev: Math.round((kpis.unsubscribed / delivered) * 100),
            pctTotal: Math.round((kpis.unsubscribed / sent) * 100),
        },
    ];

    return (
        <Card className="rounded-2xl">
            <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                    <BarChart2 className="h-4 w-4" /> Conversions (Funnel)
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="h-64 w-full sm:h-72 md:h-80">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={rows}
                                layout="vertical"
                                margin={{ top: 10, right: 20, left: 20, bottom: 10 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                                <YAxis type="category" dataKey="step" />
                                <Tooltip formatter={(v: any) => `${v}%`} />
                                <Legend wrapperStyle={{ paddingTop: 8 }} />
                                <Bar dataKey="pctPrev" name="vs previous step" fill="#8B5CF6" />
                                <Bar dataKey="pctTotal" name="vs sent (total)" fill="#60A5FA" />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

// Special value for "All" option in selects (Radix UI doesn't allow empty strings)
const ALL_VALUE = "__all__";

export function AnalyticsView() {
    // Filter state
    const [timeRange, setTimeRange] = useState("30d");
    const [chartKind, setChartKind] = useState<"line" | "bar" | "conversions">("line");
    const [granularity, setGranularity] = useState<"daily" | "weekly" | "monthly">("daily");
    const [notificationSource, setNotificationSource] = useState<NotificationSource>("all");
    const [selectedCampaignId, setSelectedCampaignId] = useState<string>(ALL_VALUE);

    // Calculate date range from time range selector
    const { startDate, endDate } = useMemo(() => getDateRange(timeRange), [timeRange]);

    // Convert filter values for API calls (ALL_VALUE -> undefined)
    const campaignIdForApi = selectedCampaignId === ALL_VALUE ? undefined : selectedCampaignId;

    // Fetch filter options
    const { data: filterOptions, isLoading: isLoadingFilters } = trpc.pushNotifications.getAnalyticsFilterOptions.useQuery();

    // Fetch global opt-in stats
    const { data: optInStats, isLoading: isLoadingOptIn } = trpc.pushNotifications.getGlobalOptInStats.useQuery();

    // Fetch KPIs
    const { data: kpis, isLoading: isLoadingKPIs } = trpc.pushNotifications.getPushNotificationKPIs.useQuery({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        notificationSource,
        campaignId: campaignIdForApi,
    });

    // Fetch time series analytics
    const { data: analyticsData, isLoading: isLoadingAnalytics } = trpc.pushNotifications.getPushNotificationAnalytics.useQuery({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        notificationSource,
        campaignId: campaignIdForApi,
        granularity,
    });

    // Aggregate data for bar chart
    const barData = useMemo(() => {
        if (!analyticsData) return [];
        return aggregate(analyticsData as AnalyticsDataPoint[], granularity);
    }, [analyticsData, granularity]);

    // Default values
    const kpiValues = kpis ?? { sent: 0, delivered: 0, opened: 0, unsubscribed: 0 };
    const optInValues = optInStats ?? { totalUsers: 0, optInUsers: 0, optOutUsers: 0 };
    const chartData = (analyticsData ?? []) as AnalyticsDataPoint[];

    return (
        <div className="space-y-6">
            <GlobalOptInSummary 
                totalUsers={optInValues.totalUsers}
                optInUsers={optInValues.optInUsers}
                optOutUsers={optInValues.optOutUsers}
                isLoading={isLoadingOptIn}
            />
            
            <Card className="rounded-2xl">
                <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <Inbox className="h-4 w-4" /> Filters
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <div className="flex flex-wrap items-center gap-3">
                        {/* Time Range */}
                        <div className="flex items-center gap-2">
                            <Label className="text-xs">Time Range</Label>
                            <Select value={timeRange} onValueChange={setTimeRange}>
                                <SelectTrigger className="h-8 w-36 rounded-xl text-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="7d">Last 7 days</SelectItem>
                                    <SelectItem value="30d">Last 30 days</SelectItem>
                                    <SelectItem value="90d">Last 90 days</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Notification Source */}
                        <div className="flex items-center gap-2">
                            <Label className="text-xs">Source</Label>
                            <Select value={notificationSource} onValueChange={(v) => {
                                setNotificationSource(v as NotificationSource);
                                // Reset campaign ID when switching source
                                setSelectedCampaignId(ALL_VALUE);
                            }}>
                                <SelectTrigger className="h-8 w-48 rounded-xl text-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(NOTIFICATION_SOURCES).map(([key, label]) => (
                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Campaign ID Filter (only for campaign source) */}
                        {notificationSource === 'campaign' && filterOptions?.campaignIds && filterOptions.campaignIds.length > 0 && (
                            <div className="flex items-center gap-2">
                                <Label className="text-xs">Campaign</Label>
                                <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId}>
                                    <SelectTrigger className="h-8 w-48 rounded-xl text-xs">
                                        <SelectValue placeholder="All campaigns" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={ALL_VALUE}>All campaigns</SelectItem>
                                        {filterOptions.campaignIds.map((campaignId) => (
                                            <SelectItem key={campaignId} value={campaignId}>
                                                {campaignId.length > 30 ? `${campaignId.slice(0, 30)}...` : campaignId}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {/* Transactional Type Filter (only for transactional source) */}
                        {notificationSource === 'transactional' && filterOptions?.transactionalTypes && (
                            <div className="flex items-center gap-2">
                                <Label className="text-xs">Type</Label>
                                <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId}>
                                    <SelectTrigger className="h-8 w-64 rounded-xl text-xs">
                                        <SelectValue placeholder="All types" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={ALL_VALUE}>All types</SelectItem>
                                        {filterOptions.transactionalTypes.map((type) => (
                                            <SelectItem key={type} value={type}>
                                                {type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-wrap items-center gap-3 pt-2 border-t">
                        {/* Chart Type */}
                        <div className="flex items-center gap-2">
                            <Label className="text-xs">Chart</Label>
                            <Select value={chartKind} onValueChange={(v: any) => setChartKind(v)}>
                                <SelectTrigger className="h-8 w-36 rounded-xl text-xs">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="line">Line</SelectItem>
                                    <SelectItem value="bar">Bar</SelectItem>
                                    <SelectItem value="conversions">Conversions</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Granularity (for bar chart) */}
                        {chartKind === "bar" && (
                            <div className="flex items-center gap-2">
                                <Label className="text-xs">Granularity</Label>
                                <Select value={granularity} onValueChange={(v: any) => setGranularity(v)}>
                                    <SelectTrigger className="h-8 w-36 rounded-xl text-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="daily">Daily</SelectItem>
                                        <SelectItem value="weekly">Weekly</SelectItem>
                                        <SelectItem value="monthly">Monthly</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                <KPI label="Amount sent" value={kpiValues.sent.toLocaleString()} isLoading={isLoadingKPIs} />
                <KPI label="Amount delivered" value={kpiValues.delivered.toLocaleString()} isLoading={isLoadingKPIs} />
                <KPI label="Amount opened" value={kpiValues.opened.toLocaleString()} isLoading={isLoadingKPIs} />
                <KPI label="Amount unsubscribed" value={kpiValues.unsubscribed.toLocaleString()} isLoading={isLoadingKPIs} />
            </div>

            {chartKind === "line" ? (
                <AnalyticsLineChart data={chartData} isLoading={isLoadingAnalytics} />
            ) : chartKind === "bar" ? (
                <AnalyticsBarChart data={barData} isLoading={isLoadingAnalytics} />
            ) : (
                <AnalyticsConversions kpis={kpiValues} isLoading={isLoadingKPIs} />
            )}
        </div>
    );
}
